import { Anchor, Badge, Button, Group, List, Stack, Text, TextInput, Title } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { isApiError } from "../../api/errors";
import { useFindStaffUser } from "../../api/generated/endpoints/staff/staff";
import { ErrorAlert } from "../../platform/ErrorAlert";

/** A user by email, with the teams they are in (A7: `GET /v1/staff/users?email=`). */
export function FindUserForm() {
  const [draft, setDraft] = useState("");
  const [email, setEmail] = useState<string | undefined>(undefined);
  const found = useFindStaffUser(
    { email: email ?? "" },
    { query: { enabled: email !== undefined, retry: false } },
  );
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = draft.trim().toLowerCase();
    if (value !== "") setEmail(value);
  };
  return (
    <Stack gap="xs" maw={520}>
      <Title order={2} size="h4">
        Find a user
      </Title>
      <form onSubmit={submit} noValidate>
        <Group align="flex-end" gap="xs">
          <TextInput
            label="Email"
            type="email"
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <Button type="submit" variant="light" loading={found.isFetching}>
            Find
          </Button>
        </Group>
      </form>
      {email !== undefined && isApiError(found.error, "NOT_FOUND") ? (
        <Text size="sm" role="alert">
          No user has the email {email}.
        </Text>
      ) : null}
      {found.isError && !isApiError(found.error, "NOT_FOUND") ? (
        <ErrorAlert error={found.error} />
      ) : null}
      {found.data === undefined ? null : (
        <Stack gap={4}>
          <Group gap="xs">
            <Text fw={600}>{found.data.name ?? found.data.email}</Text>
            {found.data.is_staff ? <Badge size="xs">Staff</Badge> : null}
            {found.data.deleted_at === null ? null : (
              <Badge size="xs" color="gray">
                Deleted
              </Badge>
            )}
          </Group>
          <Text size="sm" c="var(--cai-color-text-muted)">
            {found.data.email}
          </Text>
          {found.data.memberships.length === 0 ? (
            <Text size="sm">In no team.</Text>
          ) : (
            <List size="sm">
              {found.data.memberships.map((membership) => (
                <List.Item key={membership.team_id}>
                  <Anchor
                    renderRoot={(props) => (
                      <Link
                        {...props}
                        to="/staff/teams/$teamId"
                        params={{ teamId: membership.team_id }}
                      />
                    )}
                  >
                    {membership.team_name}
                  </Anchor>
                  , {membership.role}
                </List.Item>
              ))}
            </List>
          )}
        </Stack>
      )}
    </Stack>
  );
}
